import { AVAILABILITY_MODE } from 'app/typings/core/admin/experiences/enum/availabilityMode';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type { ExperienceResponse } from 'app/typings/core/admin/experiences/experience';
import type {
  CreateExperienceRequest,
  UpdateExperienceRequest,
} from 'app/typings/core/admin/experiences/experienceForm';
import type {
  Opening,
  OpeningHours,
  TimeRange,
} from 'app/typings/core/admin/experiences/openingHours/openingHours';
import {
  NOT_APPLICABLE_OPENING_HOURS_TYPE,
  OpeningHoursType,
} from 'app/typings/core/admin/experiences/openingHours/openingHours';
import { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';
import type { ExperienceDurationDescription } from 'app/typings/core/common/experiences/enum/experienceDurationDescription';
import { EXPERIENCE_DURATION_DESCRIPTION } from 'app/typings/core/common/experiences/enum/experienceDurationDescription';
import type { PriceConfigurationType } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import {
  PRICE_COST_TYPE,
  PRICE_DIFFERENTIATION_MODE,
} from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import type {
  DayOfWeekStockConfiguration,
  StockConfigurationType,
} from 'app/typings/core/common/openingConfiguration/stockConfigurationType';
import { pickUpdatedFields } from 'app/utils/object';

const toDurationDescription = (
  value: string | null | undefined
): ExperienceDurationDescription | null =>
  Object.values<string | null | undefined>(
    EXPERIENCE_DURATION_DESCRIPTION
  ).includes(value)
    ? (value as ExperienceDurationDescription)
    : null;

export const mapExperienceToCreateRequest = (
  experience: Pick<
    ExperienceResponse,
    | 'hotelId'
    | 'countryId'
    | 'categoryId'
    | 'duration'
    | 'durationDescription'
    | 'time'
    | 'brand'
    | 'location'
    | 'emoji'
  >
): CreateExperienceRequest => ({
  hotelId: experience.hotelId,
  countryId: experience.countryId,
  categoryId: experience.categoryId,
  duration: experience.duration ?? undefined,
  durationDescription:
    toDurationDescription(experience.durationDescription) ?? undefined,
  time: experience.time || undefined,
  brand: experience.brand || undefined,
  location: experience.location || undefined,
  emoji: experience.emoji,
});

const patchValue = <T>(value: T | null | undefined): T | null | undefined => {
  if (value === undefined) {
    return undefined;
  }

  if (value === null) {
    return null;
  }

  return value;
};

const mapStockConfiguration = (
  stockConfiguration: StockConfigurationType | undefined,
  showInInventory?: boolean
): StockConfigurationType | undefined => {
  if (!stockConfiguration) {
    return stockConfiguration;
  }

  if (stockConfiguration.type === OPENING_CONFIGURATION_TYPE.DAILY) {
    const mappedDays = Object.values(DAY_OF_WEEK).reduce((acc, day) => {
      const dayConfig = stockConfiguration.days?.[day];

      acc[day] = {
        isClosed: dayConfig?.isClosed ?? false,
        stock: dayConfig?.isClosed
          ? 0
          : dayConfig?.stock !== undefined
            ? dayConfig.stock
            : null,
      };

      return acc;
    }, {} as DayOfWeekStockConfiguration);

    return {
      type: OPENING_CONFIGURATION_TYPE.DAILY,
      days: mappedDays,
    };
  }

  const stock =
    !showInInventory || !stockConfiguration.configuration?.stock
      ? null
      : stockConfiguration.configuration.stock;

  return {
    type: OPENING_CONFIGURATION_TYPE.CONSTANT,
    configuration: { stock },
  };
};

const mapNightStockConfiguration = (
  stockConfiguration: StockConfigurationType | undefined
): StockConfigurationType => {
  const days =
    stockConfiguration && 'days' in stockConfiguration
      ? stockConfiguration.days
      : undefined;

  const mappedDays = Object.values(DAY_OF_WEEK).reduce((acc, day) => {
    const isClosed = days?.[day]?.isClosed ?? false;

    acc[day] = { isClosed, stock: isClosed ? 0 : null };

    return acc;
  }, {} as DayOfWeekStockConfiguration);

  return { type: OPENING_CONFIGURATION_TYPE.DAILY, days: mappedDays };
};

const mapPriceConfiguration = (
  priceConfiguration: PriceConfigurationType | undefined,
  isAlwaysIncludedWithNight?: boolean
): PriceConfigurationType | undefined => {
  if (!priceConfiguration) {
    return priceConfiguration;
  }

  if (priceConfiguration.cost === PRICE_COST_TYPE.FREE) {
    return {
      cost: undefined,
      priceDifferentiationMode: isAlwaysIncludedWithNight
        ? PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME
        : PRICE_DIFFERENTIATION_MODE.NONE,
      type: OPENING_CONFIGURATION_TYPE.CONSTANT,
      configuration: isAlwaysIncludedWithNight
        ? { daytimePrice: 0 }
        : { price: 0 },
    };
  }

  // Only emit the fields of the active type, so a CONSTANT <-> DAILY switch does
  // not leave the opposite key (`configuration` vs `days`) in the payload.
  if (priceConfiguration.type === OPENING_CONFIGURATION_TYPE.DAILY) {
    return {
      type: OPENING_CONFIGURATION_TYPE.DAILY,
      priceDifferentiationMode: priceConfiguration.priceDifferentiationMode,
      cost: priceConfiguration.cost,
      days: priceConfiguration.days,
    };
  }

  return {
    type: OPENING_CONFIGURATION_TYPE.CONSTANT,
    priceDifferentiationMode: priceConfiguration.priceDifferentiationMode,
    cost: priceConfiguration.cost,
    configuration: priceConfiguration.configuration,
  };
};

const mapOpeningConfiguration = (
  openingConfiguration: ExperienceResponse['openingConfiguration'],
  isAlwaysIncludedWithNight?: boolean,
  showInInventory?: boolean,
  isTimeslotDaytimeOnly?: boolean
): ExperienceResponse['openingConfiguration'] => {
  if (!openingConfiguration) {
    return openingConfiguration;
  }

  return {
    ...openingConfiguration,
    stockConfiguration: isTimeslotDaytimeOnly
      ? mapNightStockConfiguration(openingConfiguration.stockConfiguration)
      : mapStockConfiguration(
          openingConfiguration.stockConfiguration,
          showInInventory
        ),
    priceConfiguration: mapPriceConfiguration(
      openingConfiguration.priceConfiguration,
      isAlwaysIncludedWithNight
    ),
  };
};

type RawOpeningHours = {
  type: string;
  isAllDay?: boolean;
  slots?: TimeRange[];
  days?: Record<string, Opening & { enabled?: boolean }>;
};

export const mapOpeningHours = (
  openingHours: RawOpeningHours | null | undefined
): OpeningHours | null | undefined => {
  if (!openingHours) {
    return openingHours;
  }

  if (openingHours.type === NOT_APPLICABLE_OPENING_HOURS_TYPE) {
    return null;
  }

  if (openingHours.type === OpeningHoursType.WEEKLY) {
    if (openingHours.isAllDay) {
      return {
        type: OpeningHoursType.WEEKLY,
        isAllDay: true,
      };
    }

    return {
      type: OpeningHoursType.WEEKLY,
      isAllDay: openingHours.isAllDay ?? false,
      slots: openingHours.slots ?? [],
    };
  }

  if (openingHours.type === OpeningHoursType.DAILY) {
    const filteredDays: Partial<
      Record<
        string,
        { isAllDay?: boolean; slots?: { start: string; end: string }[] }
      >
    > = {};

    Object.entries(openingHours.days ?? {}).forEach(
      ([day, dayOpening]: [string, Opening & { enabled?: boolean }]) => {
        if (dayOpening.isAllDay) {
          filteredDays[day] = {
            isAllDay: true,
          };
        } else {
          if (dayOpening.slots && dayOpening.slots.length) {
            filteredDays[day] = {
              isAllDay: false,
              slots: (dayOpening.slots || []).filter(
                (slot: TimeRange) => slot && slot.start && slot.end
              ),
            };
          }
        }
      }
    );

    return {
      type: OpeningHoursType.DAILY,
      days: filteredDays,
    } as OpeningHours;
  }

  const resolvedType = openingHours.days
    ? OpeningHoursType.DAILY
    : OpeningHoursType.WEEKLY;

  return mapOpeningHours({ ...openingHours, type: resolvedType });
};

const createExhaustiveFieldsList = <
  T extends Record<keyof UpdateExperienceRequest, true>,
>(
  fields: T
): (keyof T)[] => Object.keys(fields) as (keyof T)[];

type ExperienceUpdateValues = Partial<
  Omit<ExperienceResponse, 'id' | 'createdAt' | 'updatedAt'>
>;

const buildUpdatePayload = (
  experience: ExperienceUpdateValues
): UpdateExperienceRequest => {
  const updateFields = createExhaustiveFieldsList({
    categoryId: true,
    description: true,
    duration: true,
    brand: true,
    openingHours: true,
    spaceIds: true,
    durationDescription: true,
    time: true,
    roomServicePrice: true,
    accessMode: true,
    closingPeriods: true,
    maxQuantity: true,
    availableDayLimits: true,
    isRoomServiceAvailable: true,
    isRoomServiceIncluded: true,
    location: true,
    closedDaysBeforeCheckIn: true,
    tagline: true,
    minPax: true,
    maxPax: true,
    allowedPaxTypes: true,
    isAlwaysIncludedWithNight: true,
    openingConfiguration: true,
    coverPicture: true,
    emoji: true,
    experiencePictures: true,
    menu: true,
    treatmentList: true,
    importantInformation: true,
    showInInventory: true,
    availabilityMode: true,
    isTimeslotDaytimeOnly: true,
  });

  return Object.fromEntries(
    updateFields
      .filter((field) => field in experience)
      .map((field) => {
        switch (field) {
          case 'openingConfiguration':
            return [
              field,
              patchValue(
                mapOpeningConfiguration(
                  experience['openingConfiguration'],
                  experience['isAlwaysIncludedWithNight'],
                  experience['showInInventory'],
                  experience['availabilityMode'] ===
                    AVAILABILITY_MODE.TIMESLOT &&
                    experience['isTimeslotDaytimeOnly'] === true
                )
              ),
            ];

          case 'openingHours':
            return [
              field,
              patchValue(mapOpeningHours(experience['openingHours'])),
            ];

          case 'brand':
          case 'duration':
          case 'location':
          case 'time':
            return [field, experience[field] || null];

          case 'durationDescription':
            return [field, toDurationDescription(experience[field])];

          default:
            return [field, patchValue(experience[field])];
        }
      })
  ) as UpdateExperienceRequest;
};

export const mapExperienceToUpdateRequest = (
  experience: ExperienceUpdateValues,
  initialExperience?: ExperienceUpdateValues
): Partial<UpdateExperienceRequest> => {
  const payload = buildUpdatePayload(experience);

  if (!initialExperience) {
    return payload;
  }

  return pickUpdatedFields(payload, buildUpdatePayload(initialExperience));
};
